if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        QuickAddModal.bindPrefetch();
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      // ---- section fetch + prefetch -------------------------------------
      // show() used to fetch the whole product URL: 420 KB for brown-smoothie,
      // parsed with DOMParser on the main thread, then most of it deleted by
      // the EVERAW_STRIP list below because our PDP is a long marketing page.
      // It now fetches sections/quick-add-product.liquid, which renders only
      // what the modal needs, so those bytes are never sent or parsed.
      //
      // NOT ?section_id=main-product: a section rendered outside a template
      // gets its SCHEMA defaults, not the template's block settings, so that
      // came back without the pack size selector, quantity input or button
      // label. Measured 2026-08-27.
      static QUICK_ADD_SECTION = 'quick-add-product';

      // Parsed markup per product URL, so reopening the same card is instant
      // and a prefetch is never wasted. Lives for the page, not the session.
      static cache = new Map();

      static sectionUrl(productUrl) {
        const [path, query] = productUrl.split('?');
        const params = new URLSearchParams(query || '');
        params.set('section_id', QuickAddModal.QUICK_ADD_SECTION);
        return `${path}?${params.toString()}`;
      }

      static fetchSection(productUrl) {
        const cached = QuickAddModal.cache.get(productUrl);
        if (cached) return cached;

        const pending = fetch(QuickAddModal.sectionUrl(productUrl))
          .then((response) => {
            if (!response.ok) throw new Error(`quick-add section ${response.status}`);
            return response.text();
          })
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const el = doc.querySelector('product-info');
            if (!el) throw new Error('quick-add section returned no product-info');

            // Warm the section's stylesheets NOW rather than at click time.
            // section-main-product.css alone is 47 KB and is not loaded on a
            // collection page, so without this the modal opens and then waits
            // on render-blocking CSS it only started fetching on the click.
            // That was the residual slowness after the HTML was cut 8.9x.
            QuickAddModal.warmStyles(doc);

            return el;
          })
          .catch((error) => {
            // A failed prefetch must not poison the cache, or the click that
            // follows would replay the same rejection forever.
            QuickAddModal.cache.delete(productUrl);
            throw error;
          });

        QuickAddModal.cache.set(productUrl, pending);
        return pending;
      }

      // Pull the section's <link rel="stylesheet"> hrefs into the host document
      // so the browser downloads and parses them during the hover, not after
      // the click. The same links are re-injected with the markup later; the
      // browser serves those from cache. Idempotent via a seen-set.
      static warmedStyles = new Set();

      static warmStyles(doc) {
        doc.querySelectorAll('link[rel="stylesheet"][href]').forEach((link) => {
          const href = link.getAttribute('href');
          if (!href || QuickAddModal.warmedStyles.has(href)) return;
          QuickAddModal.warmedStyles.add(href);
          if (document.querySelector(`link[href="${CSS.escape(href)}"]`)) return;

          const preload = document.createElement('link');
          preload.rel = 'stylesheet';
          preload.href = href;
          // Not render-blocking for the host page: it is only needed once the
          // modal opens, and by then it is parsed.
          preload.media = 'print';
          preload.onload = () => {
            preload.media = 'all';
          };
          document.head.appendChild(preload);
        });
      }

      // Warm the cache on intent rather than on click. Passive listeners, and
      // `once` so a card that is hovered repeatedly still fetches once.
      static prefetch(opener) {
        const url = opener.getAttribute('data-product-url');
        if (url) QuickAddModal.fetchSection(url).catch(() => {});
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const productUrl = opener.getAttribute('data-product-url');

        QuickAddModal.fetchSection(productUrl)
          .then((sourceElement) => {
            // Clone: the cached element is reused on every reopen, and
            // preprocessHTML mutates what it is given.
            const productElement = sourceElement.cloneNode(true);

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .catch((error) => {
            console.error('[quick-add]', error);
            // Never strand the customer on a dead button: fall back to the PDP.
            if (productUrl) window.location.href = productUrl;
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      // One delegated pair of listeners for the whole document rather than two
      // per card. pointerenter covers mouse and pen; touchstart covers taps,
      // which fires early enough to overlap the fetch with the tap gesture.
      static bindPrefetch() {
        if (QuickAddModal.prefetchBound) return;
        QuickAddModal.prefetchBound = true;

        const onIntent = (event) => {
          const opener = event.target.closest?.('.quick-add__submit[data-product-url]');
          if (opener) QuickAddModal.prefetch(opener);
        };

        document.addEventListener('pointerover', onIntent, { passive: true });
        document.addEventListener('touchstart', onIntent, { capture: true, passive: true });
        document.addEventListener('focusin', onIntent, { passive: true });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // Everaw: show() injects the ENTIRE product page into this popup, and our
        // PDP is a full marketing page — so the quick-add modal was rendering the
        // reviews/FAQ/ingredients nav, the "what goes into your smoothie"
        // breakdown, the long description and a Shark Tank badge sitting on top of
        // the close button. A quick-add only needs media, title, price, pack size
        // and add-to-cart, so strip the long-form blocks. Selectors that do not
        // match (non-smoothie PDPs) are simply no-ops.
        const EVERAW_STRIP = [
          '.pdp_link_tab', // REVIEWS / FAQ'S / INGREDIENTS nav (mobile + desktop copies)
          '.ct-pdp-ingredient_wrp', // "What goes into your smoothie" breakdown
          '.product__description', // long description + "Read more"
          '.ct-pdp_variant', // stray ASSORTED PACK row, duplicates the pack-size selector
          '.shark-tank-badge', // absolutely positioned, lands over the modal close button
        ];
        EVERAW_STRIP.forEach((selector) => {
          productElement.querySelectorAll(selector).forEach((el) => el.remove());
        });
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
